/**
 * Thin HTTP client used by the host to call any runtime plugin's sidecar.
 *
 * All plugins expose the same contract (/trigger/{id}, /result/{id},
 * /quota, /cost, /manifest) — the client is generic. Vendor-specific
 * logic lives inside each sidecar.
 */

import { useAuthStore } from '../../stores/authStore';
import type { LoadedRuntimePlugin } from './types';

export interface TriageResult {
  study_id: string;
  status: 'pending' | 'success' | 'skipped' | 'error';
  zone?: string;              // triage: Normal / Uncertain / Abnormal
  score?: number;
  action?: string;
  interpretation?: string;
  heatmap_url?: string;
  skipped_reason?: string;
  error?: string;
  lira_upload_id?: number;    // vendor-specific extras pass through as-is
  latency_ms?: number;
  // Utility-category plugins return their own shape; the host code paths
  // that care about those fields key off plugin.manifest.category.
  [key: string]: unknown;
}

export interface QuotaResponse {
  [key: string]: unknown;
}

async function pluginFetch<T>(
  plugin: LoadedRuntimePlugin,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = `${plugin.baseUrl}${path}`;
  // Forward the current user's EdgePACS acctoken so the sidecar can fetch
  // DICOM over its loopback hop to atlrestapi. The sidecar runs on the
  // same host as EdgePACS but has no login of its own — this is the
  // least-privilege way to let it act on behalf of the caller.
  //
  // Uses the standard Authorization header rather than a custom X-...
  // header because IIS/ARR strips non-standard headers by default (same
  // gotcha the RMAI proxy had to work around via allowedServerVariables).
  // EdgePACS accepts `Authorization: Bearer <acctoken>` natively, so the
  // same header shape works end-to-end: browser → IIS → sidecar → EdgePACS.
  const acctoken = useAuthStore.getState().acctoken;
  const resp = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(acctoken ? { Authorization: `Bearer ${acctoken}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!resp.ok) {
    throw new Error(`${plugin.manifest.id} ${path} HTTP ${resp.status}`);
  }
  return (await resp.json()) as T;
}

/** Fire a plugin for a given study (or image / artifact id). */
export function triggerPlugin(
  plugin: LoadedRuntimePlugin,
  studyId: string,
): Promise<TriageResult> {
  return pluginFetch<TriageResult>(plugin, `/triage/study/${encodeURIComponent(studyId)}`, {
    method: 'POST',
  });
}

/** Fetch the current cached result. Returns status='pending' if not yet
 *  triggered — the host uses this to render a skeleton badge. */
export function getPluginResult(
  plugin: LoadedRuntimePlugin,
  studyId: string,
): Promise<TriageResult> {
  return pluginFetch<TriageResult>(plugin, `/triage/study/${encodeURIComponent(studyId)}`);
}

/** Bulk trigger — one request fires many studies through the plugin. The
 *  shape returned is {count, success, skipped, errors, results}. */
export function triggerPluginBulk(
  plugin: LoadedRuntimePlugin,
  studyIds: string[],
): Promise<{
  count: number;
  success: number;
  skipped: number;
  errors: number;
  results: TriageResult[];
}> {
  return pluginFetch(plugin, '/triage/bulk', {
    method: 'POST',
    body: JSON.stringify({ study_ids: studyIds }),
  });
}

/** Vendor-specific quota (LIRA: remaining_chest_xray + allowed_requests;
 *  future vendors return their own shape). */
export function getPluginQuota(plugin: LoadedRuntimePlugin): Promise<QuotaResponse> {
  return pluginFetch<QuotaResponse>(plugin, '/triage/quota');
}
